import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TestID } from "../../shared/testids/testids";
import { CORETooltip } from "../Overlay/CORETooltip";
import "./CORETruncateText.less";
import { TooltipPlacement } from "antd/lib/tooltip";

const getTooltipContent = (
  message: React.ReactNode,
  messageTooltip: React.ReactNode
): React.ReactNode => {
  if (messageTooltip) return messageTooltip;
  return message;
};

export type CORETruncateTextProps = {
  children: React.ReactNode;
  truncateAfter?: number;
  messageTooltip?: React.ReactNode;
  testID: TestID;
  tooltipPosition?: TooltipPlacement;
  tooltipWidth?: string | "auto";
};

export const CORETruncateText: React.FC<CORETruncateTextProps> = ({
  children,
  messageTooltip,
  testID,
  truncateAfter,
  tooltipPosition = "top",
  tooltipWidth = "auto",
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkIsTruncated = useCallback(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const needsTruncation =
      contentElement.scrollHeight > contentElement.clientHeight;
    setIsTruncated((prev) =>
      prev !== needsTruncation ? needsTruncation : prev
    );
  }, []);

  useLayoutEffect(() => {
    if (!truncateAfter) return;
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const observer = new ResizeObserver(() => {
      requestIdleCallback(checkIsTruncated);
    });

    observer.observe(contentElement);
    checkIsTruncated();

    return () => observer.disconnect();
  }, [truncateAfter, children, checkIsTruncated]);

  const tooltipContent = useMemo(
    () => getTooltipContent(children, messageTooltip),
    [children, messageTooltip]
  );

  const hideTooltip = messageTooltip ? false : !isTruncated;

  return (
    <CORETooltip
      title=""
      message={tooltipContent}
      position={tooltipPosition}
      width={tooltipWidth}
      testID={`${testID}-tooltips` as TestID}
      hidden={hideTooltip}
      className="core-truncate-text-with-tooltip"
    >
      <div
        ref={contentRef}
        className="core-truncate-text"
        style={
          truncateAfter
            ? {
                lineClamp: truncateAfter,
                WebkitLineClamp: truncateAfter,
              }
            : {}
        }
      >
        {children}
      </div>
    </CORETooltip>
  );
};
